import { useEffect, useState } from "react";
import api from "../api/client";
import { Plus, Trash2, Shield, ShieldOff, FileCheck2, FileX2, RotateCcw, Download, FileSpreadsheet, Send, Mail, Upload } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "../lib/download";
import AddWorkerDialog from "./AddWorkerDialog";
import BulkImportDialog from "./BulkImportDialog";
import SecurityQADialog from "./SecurityQADialog";
import WorkerScheduleCard from "./WorkerScheduleCard";
import logger from "../lib/logger";

function fmtAcceptedDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function WorkersManager({ workers, onChange, sites = [] }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [viewingQA, setViewingQA] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");

  useEffect(() => {
    api.get("/reports/clients")
      .then((r) => setClients(r.data))
      .catch(() => setClients([]));
  }, [workers]);

  const addWorker = async (form) => {
    try {
      const { data } = await api.post("/workers", form);
      const creds = `${data.email} / ${data.temp_password || "welcome123"}`;
      if (data.welcome_email_sent) {
        toast.success(`Team member added — login details emailed to ${data.email} (starter password ${data.temp_password || "welcome123"})`, { duration: 10000 });
      } else if (form.send_welcome) {
        toast.warning(`Team member added, but the welcome email could not be sent. Pass on: ${creds}`, { duration: 15000 });
      } else {
        toast.success(`Team member added — login: ${creds} (they'll be asked to change it on first sign-in)`, { duration: 12000 });
      }
      onChange();
      return true;
    } catch (err) {
      logger.error("Add worker failed:", err);
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : (detail || err?.message || "Failed to add team member");
      toast.error(msg, { duration: 8000 });
      return false;
    }
  };

  const sendWelcome = async (w) => {
    if (!window.confirm(`Reset ${w.name}'s password to the starter password and email their login details to ${w.email}?`)) return;
    try {
      const { data } = await api.post(`/workers/${w.id}/send-welcome`);
      toast.success(`Welcome email sent to ${data.email}`);
    } catch (err) {
      logger.error("Send welcome failed:", err);
      toast.error(err?.response?.data?.detail || "Could not send welcome email");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member? Their shifts will be cleared.")) return;
    try {
      await api.delete(`/workers/${id}`);
      toast.success("Team member removed");
      onChange();
    } catch (err) {
      logger.error("Delete worker failed:", err);
      toast.error("Failed to delete");
    }
  };

  const resetTerms = async (w) => {
    if (!window.confirm(`Force ${w.name} to re-accept the Terms of Service and Privacy Policy on their next sign-in?`)) return;
    try {
      await api.post(`/workers/${w.id}/reset-terms`);
      toast.success(`${w.name} will be asked to re-accept on next sign-in`);
      onChange();
    } catch (err) {
      logger.error("Reset terms failed:", err);
      toast.error("Failed to reset terms acceptance");
    }
  };

  const exportComplianceCsv = async () => {
    try {
      await downloadCsv("/reports/team-compliance.csv", {}, "team_compliance.csv");
      toast.success("Compliance CSV downloaded");
    } catch (err) {
      logger.error("CSV download failed", err);
      toast.error("Could not download CSV");
    }
  };

  const exportWorkbook = async () => {
    try {
      const params = { weeks: 4 };
      if (clientFilter) params.client = clientFilter;
      const { data, headers } = await api.get("/reports/workbook.xlsx", {
        params, responseType: "blob",
      });
      let filename = "avidity_weekly_report.xlsx";
      const cd = headers["content-disposition"];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(clientFilter ? `Report for ${clientFilter} downloaded` : "Weekly Excel report downloaded");
    } catch (err) {
      logger.error("Workbook download failed", err);
      toast.error("Could not download Excel report");
    }
  };

  const emailWorkbookNow = async () => {
    if (!window.confirm(`Email the weekly Excel report ${clientFilter ? `for ${clientFilter} ` : ""}to the configured recipients right now?`)) return;
    try {
      const params = { weeks: 4 };
      if (clientFilter) params.client = clientFilter;
      const { data } = await api.post("/reports/weekly-report/send-now", null, { params });
      if (data.sent) {
        toast.success(`Report emailed to ${data.recipients.join(", ")}`);
      } else {
        toast.warning(
          `Report built (${(data.bytes / 1024).toFixed(1)} KB) but Resend is disabled — set RESEND_API_KEY in backend/.env`,
          { duration: 9000 },
        );
      }
    } catch (err) {
      logger.error("Email send failed:", err);
      toast.error(err?.response?.data?.detail || "Email send failed");
    }
  };

  return (
    <div className="border border-gray-300 bg-white p-5 rounded-sm" data-testid="workers-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-black text-lg tracking-tight">Team Members ({workers.length})</h3>
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#002FA7] text-white font-bold rounded-sm"
            data-testid="admin-only-badge-workers"
          >
            Admin only
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="open-bulk-import-button"
            className="border border-gray-300 px-3 py-1.5 rounded-sm flex items-center gap-1 hover:bg-gray-100 text-sm font-semibold"
            title="Paste a list or upload a CSV of names and emails"
          >
            <Upload className="w-4 h-4" /> Bulk import
          </button>
          <button
            onClick={() => setDialogOpen(true)}
            data-testid="open-add-worker-button"
            className="bg-[#002FA7] text-white px-3 py-1.5 rounded-sm flex items-center gap-1 hover:bg-[#002277] text-sm font-bold"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {clients.length > 0 && (
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            data-testid="report-client-filter"
            className="border border-gray-300 px-2 py-1.5 text-xs rounded-sm bg-white"
            title="Restrict reports to one client's sites"
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.client_name} value={c.client_name}>
                {c.client_name} ({c.site_count})
              </option>
            ))}
          </select>
        )}
        <button
          onClick={exportComplianceCsv}
          data-testid="export-compliance-csv"
          className="border border-gray-300 px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-gray-100 flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" /> Compliance CSV
        </button>
        <button
          onClick={exportWorkbook}
          data-testid="export-weekly-workbook"
          className="border border-gray-800 bg-gray-900 text-white px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-black flex items-center gap-1.5"
          title="Excel workbook: compliance + attendance + weekly services + workplaces cleaned, with charts"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" /> Weekly Report (Excel)
        </button>
        <button
          onClick={emailWorkbookNow}
          data-testid="email-weekly-workbook"
          className="border border-[#002FA7] text-[#002FA7] px-3 py-1.5 text-xs font-semibold rounded-sm hover:bg-[#F0F4FF] flex items-center gap-1.5"
          title="Email the Excel workbook to the configured recipients now (Monday 8am UTC otherwise)"
        >
          <Send className="w-3.5 h-3.5" /> Email Report Now
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto -mx-5 px-5">
        {workers.map((w) => (
          <div key={w.id} className="border-b border-gray-100" data-testid={`worker-row-${w.id}`}>
            <div className="flex items-center gap-3 py-2">
            <div
              className="w-8 h-8 grid place-items-center rounded-sm font-bold text-xs"
              style={{ background: w.color.bg, color: w.color.text, border: `1px solid ${w.color.border}` }}
            >
              {w.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{w.name}</div>
              <div className="text-xs text-gray-500 truncate font-mono">{w.email}</div>
              <div className="flex items-center gap-1 mt-0.5 text-[10px]" data-testid={`worker-terms-${w.id}`}>
                {w.accepted_terms_at ? (
                  <span
                    className="inline-flex items-center gap-0.5 text-green-700"
                    title={`Accepted ${fmtAcceptedDate(w.accepted_terms_at)}`}
                  >
                    <FileCheck2 className="w-3 h-3" />
                    Terms accepted {fmtAcceptedDate(w.accepted_terms_at)}
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-0.5 text-gray-400"
                    title="Terms not yet accepted"
                  >
                    <FileX2 className="w-3 h-3" />
                    Terms pending
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => sendWelcome(w)}
              className="text-gray-500 hover:text-[#002FA7] p-1"
              data-testid={`send-welcome-${w.id}`}
              aria-label="Send welcome email"
              title="Reset to starter password and email login details"
            >
              <Mail className="w-4 h-4" />
            </button>
            <button
              onClick={() => resetTerms(w)}
              disabled={!w.accepted_terms_at}
              className="text-gray-500 hover:text-[#002FA7] p-1 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid={`reset-terms-${w.id}`}
              aria-label="Force re-acceptance of Terms"
              title={w.accepted_terms_at ? "Force re-acceptance of Terms" : "Worker hasn't accepted yet"}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewingQA(w)}
              className={`p-1 ${w.has_security_questions ? "text-[#002FA7] hover:text-[#001E66]" : "text-gray-400 hover:text-gray-700"}`}
              data-testid={`view-sqa-${w.id}`}
              aria-label="View security questions"
              title={w.has_security_questions ? "View security Q&A" : "Set up security Q&A"}
            >
              {w.has_security_questions ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
            </button>
            <button
              onClick={() => remove(w.id)}
              className="text-red-600 hover:text-red-800 p-1"
              data-testid={`delete-worker-${w.id}`}
              aria-label="Delete team member"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            </div>
            <WorkerScheduleCard worker={w} sites={sites} />
          </div>
        ))}
      </div>

      <AddWorkerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={addWorker}
      />

      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onDone={onChange}
      />

      <SecurityQADialog
        open={!!viewingQA}
        worker={viewingQA}
        onClose={() => { setViewingQA(null); onChange(); }}
      />
    </div>
  );
}
